/**
 * *! This helper has unit tests.
 * If you make any changes to this file, you should run the unit tests:
 * npm test src/utest/addons/addon_settings_helpers.test.ts
 */

import { get } from 'svelte/store';
//Formatters
import { format } from 'svelte-i18n';
//Services
import { gen2Request } from 'src/services/gen_2_http_reqs';
//Services
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
//Helpers
import { real_id } from '../interface_helpers';
// Config
import { peripheralSettingsConfig } from 'src/configs/addon_settings_config';
//Types
import type { Device } from 'src/types/Device';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { Peripheral, PeripheralSettings } from 'src/types/Peripherals';
import type { SettingRequest } from 'src/types/requests/settings/setting';

const getPeripheralSettingsTranslation = (settingType: string, peripheralType: string) => {
	const $format = get(format);
	switch (settingType) {
		case 'name':
			return $format('sub_settings_title_peripheral_name', {
				default: 'Peripheral name',
			});
		case 'offset':
			return $format(`sub_settings_title_${peripheralType}_offset`, {
				default: `${peripheralType} offset`,
			});
		case 'threshold':
			return $format(`sub_settings_title_${peripheralType}_threshold`, {
				default: `${peripheralType} threshold`,
			});
		case 'temperature_units':
			return $format('sub_settings_title_temperature_unit', {
				default: 'Temperature units',
			});
		case 'digital_input_settings':
			return $format('sub_settings_title_digital_input_settings', {
				default: 'digital input settings',
			});
		case 'digital_input_type':
			return $format('sub_settings_title_digital_input_type', {
				default: 'digital input type',
			});
		case 'digital_input_icon':
			return $format('digital_input_status_icon', {
				default: 'Digital input status representing an icon',
			});
		case 'measurement_range':
			return $format('sub_settings_title_peripheral_measurement_range', {
				default: 'Measurement Range',
			});
		case 'voltmeter_custom_expression':
			return $format('sub_settings_title_input_counter_custom_expression_voltmeter', {
				default: 'Voltmeter Custom Expression',
			});
		case 'analog_settings':
			return $format('sub_settings_title_analog_input_settings', {
				default: 'Analog Input settings',
			});
		case 'analog_calibration':
			return $format('peripherals_analog_input_calibration', {
				default: 'Analog calibration (range map)',
			});
		case 'analog_custom_expression':
			return $format('sub_settings_title_input_counter_custom_expression_analog', {
				default: 'Analog Input Custom Expression',
			});
		case 'power_on_action':
			return $format('action_on_power_on_default_label', {
				default: 'Action on power on',
			});
		default:
			return (settingType || '').replace(/_/g, ' ');
	}
};

export function getPeripheralSettings(peripheral: Peripheral, settings: DeviceSettingsG2) {
	const submenuItems = [];
	const type = peripheral.peripheralType;
	const name = peripheral.name.split(':')[0];
	const config = peripheralSettingsConfig[name];
	if (!config) return submenuItems;
	config.settings.forEach((settingType: string) => {
		const title = getPeripheralSettingsTranslation(settingType, type);
		submenuItems.push({ title, settingType });
	});
	if (config.conditional) {
		Object.entries(config.conditional).forEach(([key, settingType]) => {
			if (settings[key] != null) {
				const title = getPeripheralSettingsTranslation(settingType as string, type);
				submenuItems.push({ title, settingType });
			}
		});
	}
	return submenuItems;
}

export const savePeripheralSettings = async (
	device: Device,
	peripheral: Peripheral,
	settings: PeripheralSettings,
) => {
	const data = {
		id: settings.id,
		config: {},
	};
	for (const k in settings) {
		let val = settings[k];
		if (k != 'id' && val != null) {
			if (/^(report_thr|offset)/.test(k)) {
				val = Number(val);
			}
			data.config[k] = val;
		}
	}
	const res: SettingRequest = await gen2Request(
		device,
		`${peripheral.peripheralType}.SetConfig`,
		data,
	);
	if (res?.isok) {
		const devSettings = devices_settings.getByRealId(peripheral.deviceID);
		devSettings[peripheral.sensor.component] = settings;
		peripheral.settings.name = settings.name;
		devices_settings.update_devices_settings(peripheral.deviceID, devSettings);
	}
	return res;
};

export const syncPeripheralDeviceName = async (device: Device) => {
	const mainDevice = get(devices)[real_id(device?.id)];
	if (!mainDevice) return;
	const channel = device.component.split(':')[1];
	const res: SettingRequest = await gen2Request(mainDevice, `${device.peripheral_type}.SetConfig`, {
		id: channel,
		config: { name: device.name },
	});
	if (res?.isok) {
		const devSettings = devices_settings.getByRealId(mainDevice.id);
		if (devSettings) {
			devSettings[device.component].name = device.name;
			devices_settings.update_devices_settings(mainDevice.id, devSettings);
		}
	}
};
